import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  BarChart3, 
  Users, 
  MessageSquare, 
  DollarSign,
  TrendingUp,
  FileText,
  Star,
  Globe,
  Eye,
  Clock,
  MousePointer
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { tariffsAPI, testimonialsAPI, blogAPI, faqAPI, analyticsAPI } from '../../utils/api';
import { format, subDays } from 'date-fns';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    tariffs: 0,
    testimonials: 0,
    blogPosts: 0,
    faqItems: 0
  });
  const [trafficStats, setTrafficStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [tariffsRes, testimonialsRes, blogRes, faqRes, trafficRes] = await Promise.all([
          tariffsAPI.getAllAdmin(),
          testimonialsAPI.getAllAdmin(),
          blogAPI.getAllAdmin(),
          faqAPI.getAllAdmin(),
          analyticsAPI.getTrafficStats(7).catch(() => ({ data: null }))
        ]);

        setStats({
          tariffs: tariffsRes.data.length,
          testimonials: testimonialsRes.data.length,
          blogPosts: blogRes.data.length,
          faqItems: faqRes.data.length
        });

        if (trafficRes.data) {
          setTrafficStats(trafficRes.data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statsCards = [
    {
      title: 'Посетители (7 дней)',
      value: trafficStats?.total_visits || 0,
      description: 'Всего визитов за неделю',
      icon: Users,
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/10',
      borderColor: 'border-blue-400/20'
    },
    {
      title: 'Просмотры страниц',
      value: trafficStats?.page_views || 0,
      description: 'Страниц просмотрено',
      icon: Eye,
      color: 'text-green-400',
      bgColor: 'bg-green-400/10',
      borderColor: 'border-green-400/20'
    },
    {
      title: 'Уникальные посетители',
      value: trafficStats?.unique_visitors || 0,
      description: 'Уникальных IP адресов',
      icon: MousePointer,
      color: 'text-purple-400',
      bgColor: 'bg-purple-400/10',
      borderColor: 'border-purple-400/20'
    },
    {
      title: 'Показатель отказов',
      value: trafficStats?.bounce_rate ? `${trafficStats.bounce_rate.toFixed(1)}%` : '0%',
      description: 'Процент отказов',
      icon: TrendingUp,
      color: 'text-orange-400',
      bgColor: 'bg-orange-400/10',
      borderColor: 'border-orange-400/20'
    }
  ];

  const quickActions = [
    {
      title: 'Подробная аналитика',
      description: 'Полная статистика посетителей',
      href: '/admin/statistics',
      icon: BarChart3,
      color: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      title: 'Управление контентом',
      description: 'Редактировать главную страницу',
      href: '/admin/content',
      icon: Globe,
      color: 'bg-green-600 hover:bg-green-700'
    },
    {
      title: 'Новая статья',
      description: 'Написать статью в блог',
      href: '/admin/blog',
      icon: FileText,
      color: 'bg-purple-600 hover:bg-purple-700'
    },
    {
      title: 'Настройки сайта',
      description: 'Изменить настройки и контакты',
      href: '/admin/settings',
      icon: Users,
      color: 'bg-gray-600 hover:bg-gray-700'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Панель управления</h1>
          <p className="text-gray-400">Добро пожаловать в админ панель @lngaua_bot</p>
        </div>
        <Badge className="bg-green-600 text-white">
          Система активна
        </Badge>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Card className={`bg-gray-800 ${stat.borderColor} border`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <p className="text-xs text-gray-400 mt-1">{stat.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Content Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-300 flex items-center">
              <DollarSign className="h-4 w-4 mr-2 text-green-400" />
              Тарифы
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-white">{stats.tariffs}</div>
            <p className="text-xs text-gray-400">Активных планов</p>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-300 flex items-center">
              <Star className="h-4 w-4 mr-2 text-yellow-400" />
              Отзывы
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-white">{stats.testimonials}</div>
            <p className="text-xs text-gray-400">Отзывов клиентов</p>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-300 flex items-center">
              <FileText className="h-4 w-4 mr-2 text-blue-400" />
              Блог
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-white">{stats.blogPosts}</div>
            <p className="text-xs text-gray-400">Статей опубликовано</p>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-300 flex items-center">
              <MessageSquare className="h-4 w-4 mr-2 text-purple-400" />
              FAQ
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-white">{stats.faqItems}</div>
            <p className="text-xs text-gray-400">Вопросов и ответов</p>
          </CardContent>
        </Card>
      </div>

      {/* Traffic Chart */}
      {trafficStats && trafficStats.daily_stats && trafficStats.daily_stats.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <TrendingUp className="mr-2 h-5 w-5 text-blue-400" />
                Посещаемость за неделю
              </CardTitle>
              <CardDescription className="text-gray-400">
                Количество посетителей по дням
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trafficStats.daily_stats.slice(-7).map(day => ({
                  ...day,
                  date: format(new Date(day.date), 'dd.MM')
                }))}>
                  <XAxis dataKey="date" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1F2937', 
                      border: '1px solid #374151',
                      borderRadius: '6px',
                      color: '#F3F4F6'
                    }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="visitors" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Быстрые действия</CardTitle>
              <CardDescription className="text-gray-400">
                Часто используемые функции управления сайтом
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <a
                    key={action.title}
                    href={action.href}
                    className={`
                      flex items-center p-4 rounded-lg transition-colors ${action.color}
                      text-white hover:scale-[1.02] transform
                    `}
                  >
                    <div className="p-2 bg-white/10 rounded-lg mr-4">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium">{action.title}</div>
                      <div className="text-sm opacity-90">{action.description}</div>
                    </div>
                  </a>
                );
              })}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <TrendingUp className="mr-2 h-5 w-5 text-green-400" />
                Системная информация
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Статус API</span>
                <Badge className="bg-green-600 text-white">Активен</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">База данных</span>
                <Badge className="bg-green-600 text-white">Подключена</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Файловое хранилище</span>
                <Badge className="bg-green-600 text-white">Работает</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Последнее обновление</span>
                <span className="text-white text-sm">Сейчас</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.5 }}
      >
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Полезные ссылки</CardTitle>
            <CardDescription className="text-gray-400">
              Быстрый доступ к важным разделам
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="/"
                target="_blank"
                className="flex items-center p-3 border border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
              >
                <Globe className="h-5 w-5 text-blue-400 mr-3" />
                <div>
                  <div className="text-white font-medium">Главная страница</div>
                  <div className="text-gray-400 text-sm">Посмотреть сайт</div>
                </div>
              </a>
              
              <a
                href="https://t.me/lngaua_bot"
                target="_blank"
                className="flex items-center p-3 border border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
              >
                <MessageSquare className="h-5 w-5 text-green-400 mr-3" />
                <div>
                  <div className="text-white font-medium">Telegram бот</div>
                  <div className="text-gray-400 text-sm">@lngaua_bot</div>
                </div>
              </a>
              
              <a
                href="/admin/blog"
                className="flex items-center p-3 border border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
              >
                <FileText className="h-5 w-5 text-purple-400 mr-3" />
                <div>
                  <div className="text-white font-medium">Управление блогом</div>
                  <div className="text-gray-400 text-sm">Статьи и новости</div>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;